
import type {
  QueriesOpportunitiesData,
  QueryOpportunity,
} from '@empathybroker/tsclient-stats/dist/models/queries-opportunities/queries-opportunities-data'
import type { QueriesOpportunitiesResponse } from '@empathybroker/tsclient-stats/dist/models/queries-opportunities/queries-opportunities.response'
import type { PropType } from 'vue'
import type {
  CategoriesTreeItem,
  Dictionary,
  FeatureNode,
  ICarouselSlide,
  RegularFilterSelectedValues,
} from '../../../models'
import type { FeatureConfigProvider } from '../../../providers/interfaces/feature-config.provider'
import type { EpbButtonSize, EpbButtonType } from '../../shared/epb-button'
import type { NoDataMessage } from '../no-data.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { QueryOpportunityTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/query-opportunity-type.enum'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { CONTAINER } from '../../../config/inversify.config'
import { TYPES } from '../../../config/inversify.types'
import { ColorEnum, FeatureIdEnum, KpiEnum } from '../../../enums'

import { InsightsMixin } from '../../../mixins'
import { ROUTER_CONSTS } from '../../../router/router.consts'
import { APP_CONSTS } from '../../../store/app/app.consts'
import { CONFIG_CONSTS } from '../../../store/config/config.consts'
import { CONTEXT_CONSTS } from '../../../store/context/context.consts'
import Carousel from '../../carousel.component.vue'
import FadingScroll from '../../fading-scroll.component.vue'

import { UiPopover, UiTooltip } from '../../keen-ui'
import { EpbButton } from '../../shared/epb-button'

import { EpbText } from '../../shared/epb-text'
import Widget from '../../widget.component.vue'
import NoData from '../no-data.component.vue'
import OpportunityPerformanceEvolution from './opportunity-performance-evolution.component.vue'

export interface Action {
  id: string
  type: EpbButtonType
  size: EpbButtonSize
  icon: string
  tooltip: string
}

export interface Data {
  queriesOpportunities: QueriesOpportunitiesData[]
}

export interface ActionsSlides {
  [terms: string]: ActionSlide[]
}

export interface ActionSlide extends ICarouselSlide {
  id: string
  icon: string
  label: string
  description: string
}

export default defineComponent({
  name: 'OpportunitiesWidget',
  components: {
    Carousel,
    EpbButton,
    EpbText,
    FadingScroll,
    NoData,
    OpportunityPerformanceEvolution,
    UiPopover,
    UiTooltip,
    Widget,
  },
  mixins: [InsightsMixin],
  props: {
    hasAnalytics: Boolean,
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
    noData: Object as PropType<Dictionary<string>>,
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.title,
        ...(this.filters && { subtitle: this.noData.filters }),
        ...(this.category && { subtitle: this.noData.category }),
      }
    },
    hasDataLoaded(): boolean {
      return !!this.data?.queriesOpportunities?.length
    },
    availableFeatureIds(): FeatureIdEnum[] {
      const playNode = this.featuresTree[DomainEnum.SEARCH].find(
        (node: FeatureNode) => node.id === FeatureIdEnum.PLAY,
      )
      return playNode.children.map((node: FeatureNode) => node.id)
    },
  },
  setup() {
    return {
      ColorEnum,
    }
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-opportunities-widget',
      featureOrder: [
        FeatureIdEnum.PROMOTION,
        FeatureIdEnum.RELATED_TAGS,
        FeatureIdEnum.CONFIGURATION,
        FeatureIdEnum.SYNONYM,
      ],
      slides: {} as ActionsSlides,
      loadingData: true,
      messages: this.$epb.messages.highlights.widget.opportunities,
    }
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    }),
    showAnalytics(queryOpportunity: QueriesOpportunitiesData): boolean {
      const kpis = this.supportedKpis(queryOpportunity)
      return this.hasAnalytics && !!kpis.length
    },
    supportedKpis(queryOpportunity: QueriesOpportunitiesData): KpiEnum[] {
      const opportunityType = this.getOpportunityType(queryOpportunity)

      const supportedKpis = {
        [QueryOpportunityTypeEnum.QUERIES_CLICKS]: () => [KpiEnum.QUERIES, KpiEnum.CLICKS],
        [QueryOpportunityTypeEnum.QUERIES_FINDABILITY]: () => [KpiEnum.CLICKS, KpiEnum.FINDABILITY],
        [QueryOpportunityTypeEnum.QUERIES_NO_RESULTS]: () => [KpiEnum.QUERIES, KpiEnum.NO_RESULTS],
      }[opportunityType]()

      return this.kpis.filter((kpi: KpiEnum) => supportedKpis.includes(kpi))
    },
    getOpportunityType(queryOpportunity: QueriesOpportunitiesData): QueryOpportunityTypeEnum {
      return queryOpportunity.opportunities[0].opportunityType
    },
    getOpportunityActions(queryOpportunity: QueriesOpportunitiesData): Action[] {
      return queryOpportunity.opportunities
        .reduce((opportunityFeatures: FeatureIdEnum[], opportunity: QueryOpportunity) => {
          const features = this.convertOpportunityTypeToFeatureIdEnums(opportunity.opportunityType)
          const filteredFeatures = features.filter(
            (feature: FeatureIdEnum) => !opportunityFeatures.includes(feature),
          )
          opportunityFeatures.push(...filteredFeatures)
          return opportunityFeatures
        }, [])
        .sort(
          (feature1: FeatureIdEnum, feature2: FeatureIdEnum) =>
            this.featureOrder.indexOf(feature1) - this.featureOrder.indexOf(feature2),
        )
        .map((featureId: FeatureIdEnum): Action => {
          const getFeatureConfigProvider = CONTAINER.get<FeatureConfigProvider>(
            TYPES.FeatureConfigProvider,
          )
          const featureConfig = getFeatureConfigProvider(featureId, {
            domain: DomainEnum.SEARCH,
          })
          return {
            id: featureId,
            type: 'void',
            size: 'small',
            icon: featureConfig.icon,
            tooltip: featureConfig.name,
          }
        })
    },
    convertOpportunityTypeToFeatureIdEnums(
      opportunityType: QueryOpportunityTypeEnum,
    ): FeatureIdEnum[] {
      return {
        [QueryOpportunityTypeEnum.QUERIES_FINDABILITY]: [
          FeatureIdEnum.PROMOTION,
          FeatureIdEnum.CONFIGURATION,
        ],
        [QueryOpportunityTypeEnum.QUERIES_CLICKS]: [
          FeatureIdEnum.SYNONYM,
          FeatureIdEnum.PROMOTION,
          FeatureIdEnum.CONFIGURATION,
          FeatureIdEnum.RELATED_TAGS,
        ],
        [QueryOpportunityTypeEnum.QUERIES_NO_RESULTS]: [
          FeatureIdEnum.SYNONYM,
          FeatureIdEnum.REDIRECTION,
        ],
      }[opportunityType].filter((featureId: FeatureIdEnum) =>
        this.availableFeatureIds.includes(featureId),
      )
    },
    async onActionClick(action: Action, queryOpportunity: QueriesOpportunitiesData): Promise<void> {
      await this.setQuery(queryOpportunity.terms)
      const route = this.getFeatureRoute(action.id as FeatureIdEnum)
      this.$router.push({
        ...this.$route,
        name: route,
      })
    },
    getFeatureRoute(featureId: FeatureIdEnum): string {
      // TODO: We should be able to get the route names from the container and
      // find the one we need using the featureId.
      return {
        [FeatureIdEnum.PROMOTION]: ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION].list,
        [FeatureIdEnum.RELATED_TAGS]: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_TAGS].list,
        [FeatureIdEnum.CONFIGURATION]:
          ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH].list,
        [FeatureIdEnum.SYNONYM]: ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].list,
        [FeatureIdEnum.REDIRECTION]: ROUTER_CONSTS.routes[FeatureIdEnum.REDIRECTION].list,
      }[featureId]
    },
    async fetchData(): Promise<Data> {
      this.loadingData = true
      const trendingResponse: QueriesOpportunitiesResponse = await this.fetchStatsReport({
        id: this.category
          ? StatsReportsIdsEnum.QUERIES_OPPORTUNITIES_BY_CATEGORY
          : StatsReportsIdsEnum.QUERIES_OPPORTUNITIES,
        params: {
          ...(this.filters && { filters: this.filters }),
          ...(this.category && {
            categoryId: this.category.id,
            level: this.category.level,
            ...(this.category.level > 1 && { parentId: this.category.parentId }),
          }),
        },
      })
      this.loadingData = false
      return {
        queriesOpportunities: trendingResponse.data,
      }
    },
    getOpportunityActionsSlides(queryOpportunity: QueriesOpportunitiesData): ActionSlide[] {
      return this.getOpportunityActions(queryOpportunity).map(
        (opportunityAction: Action, opportunityIndex: number) => ({
          active: !opportunityIndex,
          id: opportunityAction.id,
          icon: opportunityAction.icon,
          label: opportunityAction.tooltip,
          description: this.getActionSlideDescription(opportunityAction, queryOpportunity),
        }),
      )
    },
    getActionSlideDescription(
      opportunityAction: Action,
      queryOpportunity: QueriesOpportunitiesData,
    ): string {
      const toolMessage = this.messages.tooltip.tools[opportunityAction.id]
      if (
        opportunityAction.id === FeatureIdEnum.RELATED_TAGS ||
        opportunityAction.id === FeatureIdEnum.REDIRECTION
      ) {
        return toolMessage
      }
      const opportunityType = this.getOpportunityType(queryOpportunity)
      const messageType = {
        [FeatureIdEnum.SYNONYM]:
          opportunityType === QueryOpportunityTypeEnum.QUERIES_NO_RESULTS ? 'noResults' : 'clicks',
        [FeatureIdEnum.PROMOTION]:
          opportunityType === QueryOpportunityTypeEnum.QUERIES_FINDABILITY
            ? 'findability'
            : 'clicks',
        [FeatureIdEnum.CONFIGURATION]:
          opportunityType === QueryOpportunityTypeEnum.QUERIES_FINDABILITY
            ? 'findability'
            : 'clicks',
      }[opportunityAction.id]
      return toolMessage[messageType]
    },
  },
  watch: {
    data: {
      handler(data: Data): void {
        this.slides = data.queriesOpportunities.reduce(
          (opportunitiesSlides: ActionsSlides, opportunity: QueriesOpportunitiesData) => {
            opportunitiesSlides[opportunity.terms] = this.getOpportunityActionsSlides(opportunity)
            return opportunitiesSlides
          },
          {},
        )
      },
    },
    filters: {
      async handler(
        newFilters: Record<string, RegularFilterSelectedValues>,
        oldFilters: Record<string, RegularFilterSelectedValues>,
      ): Promise<void> {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          if (this.data?.queriesOpportunities) {
            this.data.queriesOpportunities = []
          }
          await this.loadData()
        }
      },
    },
    category: {
      async handler(
        newCategory: CategoriesTreeItem,
        oldCategory: CategoriesTreeItem,
      ): Promise<void> {
        if (JSON.stringify(newCategory) !== JSON.stringify(oldCategory)) {
          if (this.data?.queriesOpportunities) {
            this.data.queriesOpportunities = []
          }
          await this.loadData()
        }
      },
    },
  },
})
